/**
 * OrderData
 * --
 */
import React, { useState } from 'react';
import { Content, Title } from 'components';
import { Button, Tag, Table } from 'antd';
import { stringToMoneyFormat } from 'utils';
import moment from 'moment';
import trackDelivery from 'utils/TrackDelivery';
import { ShopOrderApi, ParcelServiceApi } from 'api';
import {
  PAYMENT_METHOD_CONSTANT, // 주문 유형
  PAYMENT_STATUS_CONSTANT, // 주문의 결제상태
  PRODUCT_TYPE, // 상품유형
  DELIVERY_STATUS,
} from 'constants/ProductConstants';
import { PURCHASE_STATUS } from 'constants/OrderConstants';
import MessageAlert from 'utils';
import dayjs from 'dayjs';
import DeliveryCompany from 'constants/DeliveryCompany';

/**----------------------------------------
 * [Component] 주문목록 > 주문 데이터
 * ----------------------------------------
 */
const OrderData = ({ orderList, setIsModalVisible, type, onReload }) => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  /* ===== STATE ===== */
  console.log('orderList =====>', orderList);
  const [selectedList, setSelectedList] = useState(null);
  // console.log('selectedList ====>', selectedList);
  /* ===== VARIABLE ===== */
  const escapeExcelHtml = (value) =>
    `${value ?? ''}`
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  /* ===== FUNCTION ===== */
  /**--------------------
   * 입금확인 함수
   * --------------------
   */
  const handleDepositCheck = async () => {
    if (selectedList.length < 1) {
      MessageAlert.warning('처리할 항목을 선택해주세요');
      return;
    }
    try {
      // 입금상태가 PENDING인경우만 상태변경처리
      const flatParcelData = selectedList.flatMap(
        (item) => item.parcel_service,
      );
      const fff = flatParcelData.filter(
        (item) => item?.deposit_status === 'PENDING',
      );

      // 이미 처리된 건 제외 로직
      if (fff.length < selectedList.length) {
        if (
          !window.confirm(
            '이미 결제된 건이 포함되어 있습니다. 확인을 누르면 해당 건을 제외하고 상태를 변경합니다. 진행하시겠습니까?',
          )
        ) {
          return;
        }
      }

      let newData = fff.map((item) => ({
        shop_order_id: item.shop_order_id,
        parcel_service_id: item.parcel_service_id,
        deposit_status: 'COMPLETED',
      }));

      //
      const shopOrderData = fff
        .map((item) => ({
          shop_order_id: item.shop_order_id,
          payment_state: 'COMPLETED',
        }))
        ?.filter((item, index, self) => {
          return (
            self.findIndex((i) => i.shop_order_id === item.shop_order_id) ===
            index
          );
        });
      if (!newData || newData.length < 1) {
        return MessageAlert.info('처리 건이 없습니다.');
      }

      //shop_order 업데이트
      for (const shopOrder of shopOrderData) {
        const { status: shopOrderStatus } =
          await ShopOrderApi.updateShopOrder(shopOrder);
        if (shopOrderStatus !== 200) {
          newData = newData?.filter(
            (item) => !(item.shop_order_id === shopOrder.shop_order_id),
          );
        }
      }
      //parcel_service 업데이트
      const { status, message } =
        await ParcelServiceApi.bulkUpdateParcelService(
          newData?.map(({ shop_order_id, ...rest }) => rest),
        );
      if (status !== 200) throw { message };
      onReload();
      MessageAlert.info('변경되었습니다.');
    } catch (e) {
      MessageAlert.error(e.message);
    }
  };

  /**--------------------
   * 엑셀 다운로드
   * --------------------
   */
  const handleDownloadExel = () => {
    // setIsLoading(true);
    // console.log('orderList: ', orderList);

    try {
      const appliedDeliveryPaymentSerials = new Set();
      const paymentSerialCounts = orderList.reduce((acc, cur) => {
        const paymentSerialNumber =
          cur?.payment_serial_number ?? cur?.shop_order_id;

        acc[paymentSerialNumber] =
          (acc[paymentSerialNumber] ?? 0) + (cur?.parcel_service?.length ?? 0);

        return acc;
      }, {});
      const duplicatedPaymentSerialColors = orderList.reduce(
        (acc, cur) => {
          const paymentSerialNumber =
            cur?.payment_serial_number ?? cur?.shop_order_id;
          const isDuplicatedPaymentSerial =
            paymentSerialCounts[paymentSerialNumber] > 1;

          if (isDuplicatedPaymentSerial && !acc.colors[paymentSerialNumber]) {
            acc.colors[paymentSerialNumber] =
              acc.index % 2 === 0 ? '#fff2cc' : '#d9ead3';
            acc.index += 1;
          }

          return acc;
        },
        { colors: {}, index: 0 },
      ).colors;

      const xlsxData = orderList.reduce((acc, cur) => {
        const orderDiscountPrice =
          cur?.payment_state === 'COMPLETED'
            ? (cur?.final_discount ?? 0) + (cur?.use_point ?? 0)
            : 0;
        const parcelDiscountPrice = cur.parcel_service.reduce(
          (sum, parcel) =>
            sum +
            (parcel?.discount_price ?? 0) +
            (parcel?.product?.use_point ?? 0),
          0,
        );
        const discountRemainder = orderDiscountPrice - parcelDiscountPrice;

        const list = cur.parcel_service.map((sub, subIndex) => {
          const paymentSerialNumber =
            cur?.payment_serial_number ?? cur?.shop_order_id;
          const isFirstPaymentSerialRow =
            !appliedDeliveryPaymentSerials.has(paymentSerialNumber);
          const deliveryPrice = isFirstPaymentSerialRow
            ? (cur?.final_delivery_price ?? 0)
            : 0;

          if (isFirstPaymentSerialRow) {
            appliedDeliveryPaymentSerials.add(paymentSerialNumber);
          }

          const productPrice = sub?.product?.price ?? 0;
          const optionPrice = sub?.product?.option_price ?? 0;
          const productNum = sub?.num ?? sub?.product?.num ?? 1;
          const discountPrice =
            cur?.payment_state === 'COMPLETED'
              ? (sub?.discount_price ?? 0) +
                (sub?.product?.use_point ?? 0) +
                (subIndex === 0 ? discountRemainder : 0)
              : 0;
          const optionName = (
            sub.product?.product_option ??
            sub.product_option ??
            []
          )
            .map((opt) => opt?.option_title)
            .filter(Boolean)
            .join(' / ')
            .trim();

          return {
            __rowColor: duplicatedPaymentSerialColors[paymentSerialNumber],
            ['주문일(시간)']: dayjs
              .unix(sub.created_at)
              .format('YYYY-MM-DD HH:mm:ss'),
            ['택배사']: DeliveryCompany[sub.delivery_company]?.label,
            ['송장번호']: sub.invoice_number,
            ['받으시는 분']: cur.delivery_address.recipient_name,
            ['받으시는 분 주소']: `(${cur.delivery_address.recipient_postcode}) ${cur.delivery_address.recipient_address} ${cur.delivery_address.recipient_address_detail}`,
            ['받으시는 분 핸드폰']: cur.delivery_address.recipient_phone,
            ['상품명']: `${sub.product.product_name}`,
            ['옵션명']: optionName ?? '-',
            ['판매자']: cur.shop_information.shop_information_name,
            ['구매자']: cur.user.user_name,
            ['수량']: `${sub.product.num}`,
            ['단가']: stringToMoneyFormat(productPrice),
            ['옵션금액']: sub?.product?.option_price ?? 0,
            ['합계금액']: stringToMoneyFormat(
              (productPrice + optionPrice) * productNum,
            ),
            ['할인 금액']: stringToMoneyFormat(discountPrice) ?? 0,
            ['결제금액(배송비 제외)']: stringToMoneyFormat(sub?.payment_price),
            ['배송비']: stringToMoneyFormat(deliveryPrice) ?? 0,
            ['총 결제금액']: stringToMoneyFormat(cur?.final_price),
            ['결제수단']: PAYMENT_METHOD_CONSTANT[sub.payment_method]?.title,
            ['주문상태']: PURCHASE_STATUS[sub.purchase_status]?.title,
            ['배송상태']: DELIVERY_STATUS[sub.delivery_status]?.title,
            ['요청사항']: cur.delivery_address.request,
          };
        });

        acc = acc.concat(list);
        return acc;
      }, []);

      // console.log('xlsxData: ', xlsxData);
      // return;

      const headers = Object.keys(xlsxData[0] ?? {}).filter(
        (key) => !key.startsWith('__'),
      );
      const tableRows = xlsxData
        .map((row) => {
          const rowStyle = row.__rowColor
            ? `background-color:${row.__rowColor};`
            : '';
          const cells = headers
            .map(
              (header) =>
                `<td style="mso-number-format:'\\@';${rowStyle}">${escapeExcelHtml(
                  row[header],
                )}</td>`,
            )
            .join('');

          return `<tr>${cells}</tr>`;
        })
        .join('');
      const headerRows = headers
        .map(
          (header) =>
            `<th style="background-color:#f2f2f2;font-weight:bold;">${escapeExcelHtml(
              header,
            )}</th>`,
        )
        .join('');
      const excelHtml = `
        <html>
          <head>
            <meta charset="UTF-8" />
          </head>
          <body>
            <table border="1">
              <thead><tr>${headerRows}</tr></thead>
              <tbody>${tableRows}</tbody>
            </table>
          </body>
        </html>
      `;
      const blob = new Blob([`\ufeff${excelHtml}`], {
        type: 'application/vnd.ms-excel;charset=utf-8;',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = url;
      link.download = `주문내역_${moment().format('YYYYMMDDHHmmss')}.xls`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      // setIsLoading(false);
    } catch (e) {
      // setIsLoading(false);
      console.log(e);
      // MessageAlert.error(e.message);
    }
  };

  /* ===== HOOKS ===== */
  /* ===== RENDER ===== */
  return (
    <>
      <Content
        maxWidth={'100%'}
        borderRadius={4}
        padding={20}
        className="boxShadow"
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 10,
          }}
        >
          <Title size={5}>검색 결과 총 {orderList?.length}건</Title>
          <div>
            <Button
              size="default"
              style={{ marginLeft: 5 }}
              onClick={handleDownloadExel}
            >
              엑셀로 다운로드
            </Button>
            {/* 입금전 상태변경 => 입금확인 버튼 */}
            {type === 'PENDING' && (
              <Button
                size="default"
                style={{ marginLeft: 5 }}
                type="primary"
                disabled={selectedList === null || selectedList.length < 1}
                onClick={handleDepositCheck}
              >
                입금확인
              </Button>
            )}
          </div>
        </div>

        <Table
          data={orderList}
          // dataSource={orderList ?? []}
          dataSource={orderList.map((item) => ({
            ...item,
            key: item.shop_order_id,
          }))}
          pagination={false}
          isChecked
          bordered
          size="small"
          style={{ fontSize: '0.9em' }}
          scroll={{ y: 720, x: 750 }} // 테이블 자체 스크롤 설정
          {...(type
            ? {
                rowSelection: {
                  type: 'checkbox',
                  selectedRowKeys: selectedList?.map((item) => item.key),
                  onChange: (_selectedRowKeys, _selectedRows) => {
                    setSelectedList(_selectedRows);
                  },
                },
              }
            : {})}
          columns={[
            // {
            //   title: '택배사/송장번호',
            //   key: 'parcel_service',
            //   dataIndex: 'parcel_service',
            //   width: 135,
            //   align: 'center',
            //   fixed: 'left',
            //   style: { background: 'red' },
            //   render: (v, row) => {
            //     return v.invoice_number ? (
            //       <>
            //         <div>
            //           <span
            //             style={{
            //               fontSize: '0.8em',
            //               letterSpacing: -0.8,
            //             }}
            //           >{`${v?.delivery_company ?? ''} - ${
            //             v?.invoice_number ?? ''
            //           }`}</span>
            //         </div>
            //         <Button
            //           size="small"
            //           type="default"
            //           onClick={() => setIsModalVisible(row)}
            //         >
            //           <FormOutlined style={{ fontSize: '0.95em' }} /> 수정
            //         </Button>
            //       </>
            //     ) : (
            //       <>
            //         <Button
            //           // size="small"
            //           type="primary"
            //           onClick={() => setIsModalVisible(row)}
            //           // onClick={() => setRegisterDeliveryInfoModal(row)}
            //         >
            //           택배등록
            //         </Button>
            //       </>
            //     );
            //   },
            // },
            {
              title: (
                <>
                  주문일
                  <br />
                  (주문시간)
                </>
              ),
              key: '주문일',
              dataIndex: '주문일',
              width: 170,
              align: 'center',
              render: (_, record) =>
                moment(record.created_at * 1000).format(
                  'YYYY-MM-DD (HH:mm:ss)',
                ),
              sorter: (a, b) => a?.created_at - b?.created_at,
            },
            {
              title: '주문번호',
              key: 'shop_order_id',
              dataIndex: 'shop_order_id',
              width: 90,
              align: 'center',
              sorter: (a, b) => a?.shop_order_id - b?.shop_order_id,
            },
            {
              title: '주문 상품명',
              key: 'product_name',
              dataIndex: 'product_name',
              width: 270,
              align: 'center',
              render: (_, record) => {
                const addTxt =
                  record.product_information.length >= 2
                    ? ` 외 ${record.product_information.length - 1}`
                    : ``;
                const productName = record.product_information[0].product_name;
                // return `${productName}${addTxt}`;
                return (
                  <a
                    onClick={() => setIsModalVisible(record)}
                    style={{ textDecoration: 'underline' }}
                  >{`${productName}${addTxt}`}</a>
                );
              },
            },
            // {
            //   title: '옵션',
            //   key: 'product_option',
            //   dataIndex: 'product_option',
            //   width: 270,
            //   align: 'center',
            //   render: (_, record) => {
            //     const refineProductInformation = record?.product_information?.map((item) => item.product_option?.map((opt) => {
            //       const data = {
            //         option_title: opt?.option_title,
            //         option_type: opt?.option_type,
            //         order: opt?.order,
            //         option: opt?.option,
            //       }
            //       return data;
            //     })).flat()
            //     return (
            //       refineProductInformation.map((item) => (
            //         <li>
            //           {item?.option_title ?? null} {item?.option_title ? "/" : null} {item?.option?.map((option) => option?.option_name) ?? null}
            //         </li>
            //       ))
            //     )
            //   }
            // },
            // {
            //   title: '옵션 값',
            //   key: 'product_option_price',
            //   dataIndex: 'product_option_price',
            //   width: 120,
            //   align: 'center',
            //   render: (_, record) => {
            //     const addTxt =
            //       record.product_information?.reduce((acc, cur) => cur.option_price === "" ? acc + 0 : cur.option_price, 0);
            //     return stringToMoneyFormat(addTxt);
            //   }
            // },
            // {
            //   title: '판매자',
            //   key: 'product_name',
            //   dataIndex: 'product_name',
            //   width: 110,
            //   align: 'center',
            //   render: (_, record) => {
            //     const addTxt =
            //       record.product_information.length >= 2
            //         ? ` 외 ${record.product_information.length - 1}`
            //         : ``;
            //     const productName = record.product_information[0].product_name;
            //     return `${productName}${addTxt}`;
            //   },
            // },
            {
              title: '판매자',
              key: 'shop_information_name',
              dataIndex: 'shop_information',
              width: 110,
              align: 'center',
              render: (_, record) => {
                const shopName = record.shop_information.shop_information_name;
                return <>{shopName}</>;
              },
            },
            {
              title: '구매자',
              key: '구매자',
              dataIndex: '구매자',
              width: 100,
              align: 'center',
              render: (_, record) => {
                const userName = record.user.user_name;
                return (
                  <>
                    {/* {console.log('record', record)} */}
                    <Button size="small" color="primary" variant="link">
                      {/* <Avatar size="small">{userName[0]}</Avatar>{' '} */}
                      {userName}
                    </Button>
                  </>
                );
              },
            },
            {
              title: '결제금액',
              key: 'final_price',
              dataIndex: 'final_price',
              width: 120,
              align: 'center',
              render: (value, _record) => stringToMoneyFormat(value),
              sorter: (a, b) => a?.final_price - b?.final_price,
            },
            {
              title: '입금',
              key: 'parcel_service',
              dataIndex: 'parcel_service',
              width: 100,
              align: 'center',
              render: (value) =>
                Array.isArray(value)
                  ? value.some((item) => item.deposit_status === 'PENDING')
                    ? '입금전'
                    : value.some((item) => item.deposit_status === 'COMPLETED')
                      ? '입금완료'
                      : '결제완료'
                  : value?.deposit_status === 'PENDING'
                    ? '입금전'
                    : value?.deposit_status === 'COMPLETED'
                      ? '입금완료'
                      : '결제완료',
            },
            {
              title: '결제상태',
              key: 'application_status',
              dataIndex: 'application_status',
              width: 100,
              align: 'center',
              render: (_, record) => (
                <Tag
                  color={PAYMENT_STATUS_CONSTANT[record?.payment_state]?.color}
                >
                  {PAYMENT_STATUS_CONSTANT[record?.payment_state]?.title}
                </Tag>
              ),
            },
            {
              title: '결제수단',
              key: 'payment_method',
              dataIndex: 'payment_method',
              width: 100,
              align: 'center',
              render: (v) => {
                return (
                  <Tag color={PAYMENT_METHOD_CONSTANT[v]?.color}>
                    {PAYMENT_METHOD_CONSTANT[v]?.title}
                  </Tag>
                );
              },
            },

            {
              title: '주문일련번호',
              key: 'payment_serial_number',
              dataIndex: 'payment_serial_number',
              width: 200,
              align: 'center',
            },
            {
              title: '결제완료일',
              key: 'payment_completion_date',
              dataIndex: 'payment_completion_date',
              width: 170,
              align: 'center',
              render: (_, record) =>
                moment(record.payment_completion_date * 1000).format(
                  'YYYY-MM-DD (HH:mm:ss)',
                ),
              sorter: (a, b) =>
                a?.payment_completion_date - b?.payment_completion_date,
            },
            {
              title: '상품유형',
              key: 'type',
              dataIndex: 'type',
              width: 100,
              align: 'center',
              render: (v) => PRODUCT_TYPE[v]?.title,
              filters: [
                { text: '픽업상품', value: 'STORE_PICKUP' },
                { text: '배송상품', value: 'DELIVERY' },
              ],
              onFilter: (value, record) => record?.type?.includes(value),
            },
            {
              title: '배송비',
              key: 'final_delivery_price',
              dataIndex: 'final_delivery_price',
              width: 100,
              align: 'center',
              render: (v) => stringToMoneyFormat(v),
            },
            {
              title: '#',
              key: '상세',
              dataIndex: '상세',
              width: 90,
              align: 'center',
              fixed: 'right',
              render: (_, record) => {
                const { parcel_service } = record;

                return (
                  <>
                    <Button
                      block
                      size="small"
                      style={{ marginBottom: 2.5 }}
                      onClick={() => setIsModalVisible(record)}
                    >
                      상세
                    </Button>
                    <Button
                      block
                      size="small"
                      style={{ marginBottom: 2.5 }}
                      disabled={
                        parcel_service?.invoice_number &&
                        parcel_service?.delivery_company
                          ? false
                          : true
                      }
                      onClick={() =>
                        trackDelivery(
                          parcel_service?.delivery_company,
                          parcel_service?.invoice_number,
                        )
                      }
                    >
                      배송조회
                    </Button>
                  </>
                );
              },
            },
          ]}
        />
        {/* <Pagination
          total={orderList.length}
          //total={users?.length}
          align="center"
          style={{
            marginTop: 40,
            display: 'flex',
            justifyContent: 'center',
          }}
        /> */}
      </Content>
    </>
  );
};

/* DF */
OrderData.defaultProps = {
  orderList: [],
  onReload: () => {},
};
export default OrderData;
